const supportFAQs = {
    aiSupport: [
        { 
            question: "What should I do if I encounter issues while training my AI model?", 
            answer: "If you face issues during AI model training, ensure that your training data is correctly formatted. Check the model parameters and retry the training process. For detailed troubleshooting, visit our AI documentation or reach out to support." 
        },
        { 
            question: "How do I integrate my AI model with cloud services?", 
            answer: "To integrate your AI model with cloud services, follow our step-by-step integration guide in the documentation. Ensure that your API keys are correctly configured, and the cloud environment is set up properly. Our support team is available for assistance." 
        },
        { 
            question: "What subscription plans do you offer?", 
            answer: "We offer flexible subscription plans based on usage, including basic, standard, and enterprise plans. Each plan includes different access levels to AI services, compute power, and storage. You can compare the available plans on our pricing page." 
        },
        { 
            question: "How can I use your API for my AI integration?", 
            answer: "To integrate with our AI API, start by registering for an API key through our developer portal. You will find detailed instructions on how to authenticate, make requests, and handle responses in the API documentation." 
        },
        { 
            question: "How can I view or manage my billing details?", 
            answer: "You can manage your billing details through your account dashboard. Visit the 'Billing' section to view invoices, update payment methods, and manage subscriptions. For assistance, contact our billing support team." 
        },
        { 
            question: "How can I check the service status of your AI platform?", 
            answer: "To check for service interruptions or scheduled maintenance, visit our service status page. You can enter your region to check the status of our servers and receive real-time updates." 
        }
    ],
    cloudServices: [
        { 
            question: "How do I set up my cloud service?", 
            answer: "To set up your cloud service, log in to your account and navigate to the setup wizard. Follow the on-screen instructions to configure your environment, including storage and compute options. Refer to our user guide for detailed instructions or contact support for assistance." 
        },
        { 
            question: "Where can I access my cloud dashboard?", 
            answer: "You can access your cloud dashboard through our website by clicking on the 'Dashboard' link in the top-right corner. Log in with your credentials to manage your cloud resources, view analytics, and adjust settings." 
        },
        { 
            question: "What should I do if I experience connectivity issues?", 
            answer: "If you encounter connectivity issues, ensure that your internet connection is stable and check your network firewall settings. Restart your devices if necessary. For further troubleshooting, visit our help center or contact technical support." 
        },
        { 
            question: "How do I back up and recover my data?", 
            answer: "To back up your data, go to the 'Backup' section in your cloud dashboard and select the files or folders you wish to back up. For recovery, navigate to the 'Restore' tab and follow the prompts. Detailed steps are available in our knowledge base." 
        },
        { 
            question: "What subscription plans do you offer?", 
            answer: "We offer several subscription plans, including Basic, Standard, and Enterprise. Each plan provides varying levels of storage, compute power, and support. Visit our pricing page to compare features and choose the plan that best suits your needs." 
        },
        { 
            question: "What security features are available for my cloud data?", 
            answer: "Our cloud services include robust security features such as end-to-end encryption, multi-factor authentication, and role-based access controls. For detailed information on securing your account and data, refer to our security guide." 
        }
    ],
    dataAnalytics: [
        { 
            question: "How do I get started with data analytics?", 
            answer: "To get started, you’ll need to define your business objectives and gather the necessary data. Our platform offers step-by-step tutorials to help you import data, set up dashboards, and begin generating insights. For a detailed guide, visit our 'Getting Started' section." 
        },
        { 
            question: "How can I integrate my data sources?", 
            answer: "You can integrate various data sources such as databases, cloud storage, and APIs. Our platform supports popular tools like Google Analytics, Excel, and SQL databases. Use the 'Data Integration' wizard in your dashboard for seamless setup." 
        },
        { 
            question: "Can I customize my dashboards?", 
            answer: "Yes, our platform allows full customization of dashboards. You can add, remove, or rearrange widgets to display key metrics and visualizations that matter most to your business. Refer to the customization guide in our knowledge base for tips." 
        },
        { 
            question: "Does the platform support real-time data analysis?", 
            answer: "Our platform supports real-time data processing, enabling you to monitor live metrics and gain immediate insights. Set up real-time dashboards and alerts to stay updated on critical changes as they happen." 
        },
        { 
            question: "How is my data secured?", 
            answer: "We prioritize data security by employing industry-standard encryption, access controls, and regular security audits. For more details, check our 'Security and Privacy Policy' section." 
        },
        { 
            question: "How can I share reports and insights?", 
            answer: "You can generate reports in various formats, including PDF, Excel, and CSV. Use our sharing options to distribute reports via email or export them directly to your cloud storage. Collaboration tools also allow you to share dashboards with team members." 
        }
    ],
    integrationIssues: [
        { 
            question: "How do I troubleshoot integration errors?", 
            answer: "Start by reviewing the error messages provided in your logs or dashboard. Ensure that API keys, credentials, and endpoints are correctly configured. Check the documentation of the integrated service for common issues and resolutions. If problems persist, contact support for further assistance."
        },
        { 
            question: "Why is my API key not working?", 
            answer: "Ensure that the API key is valid, correctly entered, and has the necessary permissions. Check if the key has expired or been revoked. Refer to the service's API documentation for further details or generate a new key if needed."
        },
        { 
            question: "What should I do if the integration is incompatible?", 
            answer: "Verify that your platform supports the service or tool you’re trying to integrate. Check for any version or dependency conflicts. If compatibility is an issue, consider using middleware solutions or alternative integrations that bridge the gap."
        },
        { 
            question: "What should I do if data fails to sync?", 
            answer: "Ensure that the connection between systems is active and stable. Check for any filters or limitations in place that might block data transfer. Refer to the integration logs for specific error messages and adjust your configuration as necessary."
        },
        { 
            question: "How do I resolve OAuth authentication issues?", 
            answer: "Ensure that your client ID and secret are correctly configured and match the settings in the service’s developer console. Verify redirect URLs and scopes. If errors persist, reauthorize the connection or consult the service's OAuth troubleshooting guide."
        },
        { 
            question: "How can I fix timeout errors during integration?", 
            answer: "Timeout errors often occur due to slow response times or network issues. Optimize your requests, ensure stable network connectivity, and consider increasing the timeout setting. Contact the service provider if their API is unresponsive."
        }
    ],
    supportDocs: [
        { 
            question: "Where can I find support documentation?", 
            answer: "Support documentation is available on our website under the 'Support' section. You can access guides, FAQs, and troubleshooting resources by searching for your specific product or service."
        },
        { 
            question: "How do I download user manuals?", 
            answer: "User manuals can be downloaded from the 'Documentation' section on our website. Simply search for your product model and click the download link next to the manual you need."
        },
        { 
            question: "How often are support documents updated?", 
            answer: "Support documents are regularly updated to reflect the latest product features, fixes, and enhancements. Check the 'Last Updated' date on each document to ensure you're viewing the most current version."
        },
        { 
            question: "Can I request additional documentation?", 
            answer: "Yes, if you need additional documentation that is not currently available, you can submit a request through our support portal. Our team will review your request and provide the necessary resources."
        },
        { 
            question: "Where can I find explanations for technical terms?", 
            answer: "Our glossary of technical terms is included in the 'Support Docs' section. It provides clear explanations and examples to help you understand complex terms and concepts."
        },
        { 
            question: "Are support documents available in other languages?", 
            answer: "Yes, many of our support documents are available in multiple languages. Use the language selector on the documentation page to view the content in your preferred language."
        }
    ],
    accountManagement: [
        { 
            question: "How do I create an account?", 
            answer: "To create an account, click the 'Sign Up' button on our website and fill in the required details, such as your email address and password. Once completed, you'll receive a confirmation email to verify your account."
        },
        { 
            question: "How can I reset my password?", 
            answer: "If you've forgotten your password, click 'Forgot Password' on the login page. Enter your registered email address, and we'll send you instructions on how to reset your password."
        },
        { 
            question: "How do I update my account information?", 
            answer: "To update your account information, log in and go to the 'Account Settings' section. From there, you can update your personal details, change your password, or modify your subscription preferences."
        },
        { 
            question: "How do I delete my account?", 
            answer: "To delete your account, please contact our support team through the 'Help' section. Note that deleting your account is permanent and cannot be undone."
        },
        { 
            question: "Can I change my subscription plan?", 
            answer: "Yes, you can change your subscription plan at any time by going to the 'Billing' section in your account settings. Choose a new plan, and your billing cycle will be adjusted accordingly."
        },
        { 
            question: "What is Two-Factor Authentication (2FA), and how do I enable it?", 
            answer: "Two-Factor Authentication adds an extra layer of security to your account. You can enable it in the 'Security Settings' section of your account. Follow the instructions to link your account to an authentication app or receive codes via SMS."
        }
    ],
    aiTraining: [
        { 
            question: "What is AI Training?", 
            answer: "AI training involves teaching a machine learning model to recognize patterns and make predictions by providing it with labeled data. This process improves the model's accuracy and performance over time."
        },
        { 
            question: "What types of data are used for AI training?", 
            answer: "AI models can be trained using various data types, including text, images, audio, and video. The choice of data depends on the task, such as natural language processing, image recognition, or speech-to-text conversion."
        },
        { 
            question: "What is the difference between supervised and unsupervised learning?", 
            answer: "In supervised learning, the model is trained on labeled data, meaning each input has a corresponding output. In contrast, unsupervised learning involves training the model on unlabeled data, allowing it to identify patterns and relationships on its own."
        },
        { 
            question: "How long does AI training take?", 
            answer: "The duration of AI training depends on factors such as the size of the dataset, complexity of the model, and computational resources available. Training can take anywhere from a few hours to several weeks."
        },
        { 
            question: "Why is data quality important in AI training?", 
            answer: "High-quality data is essential for effective AI training, as it directly impacts the model's accuracy and reliability. Poor-quality or biased data can lead to incorrect predictions and limited performance."
        },
        { 
            question: "How is an AI model evaluated?", 
            answer: "AI models are evaluated using metrics such as accuracy, precision, recall, and F1-score. These metrics help determine how well the model performs on new, unseen data."
        }
    ],
    techUpdates: [
        { 
            question: "Why are software updates important?", 
            answer: "Software updates are crucial for maintaining security, improving performance, and ensuring compatibility with new technologies. They often fix bugs and vulnerabilities, protecting your systems from potential threats."
        },
        { 
            question: "How can I enable automatic updates?", 
            answer: "To enable automatic updates, navigate to your device’s settings and find the update section. On most systems, you can toggle a switch to turn on automatic updates, ensuring your software stays current without manual intervention."
        },
        { 
            question: "How can I check for the latest updates?", 
            answer: "To check for updates, go to your system's update center or software store. Most operating systems provide a ‘Check for Updates’ button to ensure you're running the latest versions of your software."
        },
        { 
            question: "What are the risks of skipping updates?", 
            answer: "Skipping updates can expose your device to security vulnerabilities, cause compatibility issues, and lead to reduced performance. It's recommended to install updates promptly to avoid these risks."
        },
        { 
            question: "What kind of new features do updates typically include?", 
            answer: "Updates often introduce new features, such as improved user interfaces, enhanced functionality, and support for new hardware or software standards. They may also optimize system performance and fix known bugs."
        },
        { 
            question: "Can I customize my update preferences?", 
            answer: "Yes, most systems allow you to customize update preferences. You can choose when updates are installed, whether to download updates over Wi-Fi only, and which updates to prioritize."
        }
    ],

};


export default supportFAQs;